import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  CounterInitModel,
  CounterInitVariables,
  CounterModel,
  CounterVariables,
} from '../../../../models/email-template/counter.interface';

/**
 * Mockup-styled Counter block - a drop-in replacement for app-counter in
 * block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas read-only summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-block-text (image upload/segment/validation), app-field-editor-v2 and
 * app-pin sub-components and the exact same CounterVariables field names -
 * only the surrounding layout is new. Single-image toggle mirrors Main
 * Banner's own desktop/mobile pattern applied to just one image (the
 * countdown GIF has no separate mobile variant in v1).
 */
@Component({
  selector: 'app-counter-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineMediaV2Directive],
  templateUrl: './counter-v2.component.html',
  styleUrl: './counter-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CounterComponentV2 extends BaseComponent implements OnInit {
  @Input() block: CounterModel = new CounterInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new CounterInitVariables();
    }
  }

  countdownImagePlaceholder = 'Countdown Image';
  countdownLinkPlaceholder = 'Enter Countdown Redirect Link';
  countdownAltPlaceholder = 'Enter Countdown Alt Text';

  /** Drives app-block-text's own [isImage] input from outside (see Main
   *  Banner's own toggle pattern). */
  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  /** The link opened by clicking the preview image - same treatment as
   *  Main Banner's previewHref: v1's exact helper.getFullUrl(CountdownLink),
   *  plus a segment query param when a segment is selected
   *  (CountdownLinkSegmentRegex is otherwise unused metadata today, v1
   *  included). */
  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['CountdownLink']);
    const segment = this.block.variables?.['CountdownLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof CounterVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
